import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import apiClient from "../api/client";
import AmortizationTable from "./AmortizationTable";
import AmortizationChart from "./AmortizationChart";
import LoanFeesTab from "./LoanFeesTab";
import LoanSecuritySection from "./LoanSecuritySection";
import "./LoanDetailPage.css";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { can } from "../utils/permissions";
import { ltvTone, LTV_TONE_CLASS } from "../utils/ltvTone";


const CLASSIFICATION_LABELS = {
  INCOME_REAL_ESTATE: "נדל״ן מניב",
  CONSTRUCTION_FINANCE: "ליווי בנייה",
  LAND_LOAN: "הלוואת קרקע",
  BUSINESS_CREDIT: "אשראי עסקי",
  LOAN: "הלוואה",
  CREDIT_FACILITY: "מסגרת אשראי",
  OTHER: "אחר",
};

const LOAN_TYPE_LABELS = {
  SPITZER: "שפיצר",
  BULLET: "בולט",
  AMORTIZING: "סילוקין",
};

const PAYMENT_PERIOD_LABELS = {
  WEEKLY: "שבועי",
  MONTHLY: "חודשי",
  QUARTERLY: "רבעוני",
  SEMI_ANNUALLY: "חצי שנתי",
  ANNUALLY: "שנתי",
};

const RATE_TYPE_LABELS = {
  FLOATING: "משתנה",
  FIXED: "קבוע",
};

function formatNumber(n) {
  if (n == null || n === "" || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("he-IL");
}

function formatDate(d) {
  if (!d) return "—";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("he-IL");
}

function formatPct(n) {
  if (n == null || n === "" || Number.isNaN(Number(n))) return "—";
  return `${Number(n).toFixed(2)}%`;
}

function daysUntil(date) {
  if (!date) return null;
  const d = new Date(date);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.round((d - now) / (1000 * 60 * 60 * 24));
}


function getNextPayment(schedule) {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return schedule.find((row) => row.date && new Date(row.date) >= now) || null;
}

const TAB_GENERAL = 0;
const TAB_FINANCIAL = 1;
const TAB_COLLATERAL = 2;
const TAB_NOTES = 3;
const TAB_HISTORY = 4;
const TAB_FEES = 5;

const ACTION_LABELS = {
  CREATED: "נוצרה",
  UPDATED: "עודכנה",
  ENDED: "פורעה",
  DELETED: "נמחקה",
  DRAW: "משיכת אשראי",
  REPAY: "החזר אשראי",
  CREATE: "נוצר",
  UPDATE: "עודכן",
  DELETE: "נמחק",
  APPROVED: "אושר",
  REJECTED: "נדחה",
};

const ACTION_COLORS = {
  CREATED: "#dcfce7", CREATE: "#dcfce7",
  UPDATED: "#dbeafe", UPDATE: "#dbeafe",
  DRAW: "#fef9c3",
  REPAY: "#dcfce7",
  ENDED: "#f3f4f6",
  DELETED: "#fee2e2", DELETE: "#fee2e2",
};

export default function LoanDetailPage() {
  const user = useCurrentUser();
  const canViewKpis        = can(user, 'loanDetail.viewKpis');
  const canViewFinancial   = can(user, 'loanDetail.viewFinancial');
  const canViewCollateral  = can(user, 'loanDetail.viewCollateral');
  const canViewAmortTable  = can(user, 'loanDetail.viewAmortTable');
  const canViewAmortChart  = can(user, 'loanDetail.viewAmortChart');
  const canViewNotes       = can(user, 'loanDetail.viewNotes');
  const canViewHistory     = can(user, 'loanDetail.viewHistory');
  const canEditLoan        = can(user, 'loanDetail.edit');
  const { id } = useParams();
  const [loan, setLoan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [infoTab, setInfoTab] = useState(TAB_GENERAL);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [schedule, setSchedule] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  useEffect(() => {
    apiClient
      .get(`/loans/${id}`)
      .then((res) => setLoan(res.data))
      .catch((err) =>
        setError(err.response?.data?.error || err.message || "שגיאה בטעינת ההלוואה")
      )
      .finally(() => setLoading(false));
  }, [id]);

  // Background refresh after a security edit (thresholds saved / pledge or
  // guarantee released) — updates loan.basket + LTV-derived fields without
  // re-triggering the full-page loading state.
  const refetchLoan = useCallback(() => {
    apiClient.get(`/loans/${id}`).then((res) => setLoan(res.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setScheduleLoading(true);
    apiClient
      .get(`/loans/${id}/schedule`)
      .then((res) => setSchedule(Array.isArray(res.data.schedule) ? res.data.schedule : []))
      .catch(() => setSchedule([]))
      .finally(() => setScheduleLoading(false));
  }, [id]);

  useEffect(() => {
    if (infoTab !== TAB_HISTORY || !id) return;
    setHistoryLoading(true);
    apiClient
      .get(`/loans/${id}/history`)
      .then((res) => setHistory(Array.isArray(res.data) ? res.data : []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  }, [infoTab, id]);

  const nextPayment = getNextPayment(schedule);

  if (loading) {
    return (
      <div className="page">
        <div className="card">
          <div className="app-loading">טוען פרטי הלוואה...</div>
        </div>
      </div>
    );
  }

  if (error || !loan) {
    return (
      <div className="page">
        <div className="card">
          <p className="message-error">{error || "הלוואה לא נמצאה"}</p>
          <Link to="/" className="btn btn-primary" style={{ marginTop: "var(--space-md)" }}>
            חזרה לטבלת הלוואות
          </Link>
        </div>
      </div>
    );
  }

  const isActive = loan.status === "active";
  const maturityDays = daysUntil(loan.maturityDate);

  // LTV gauge — sourced from the enriched fields every `GET /loans/:id`
  // response carries (enrichLoansWithBasketLTV). `currentLTV` is only ever
  // non-null when a real percentage was computed, so it is both necessary and
  // sufficient on its own.
  const hasLTV = loan.currentLTV != null;
  // Distinguishes "collateral is pledged but no price could be resolved"
  // (NO_PRICE) from "nothing is pledged at all" (NO_COLLATERAL / no
  // enrichment ran).
  const hasNoPriceCollateral = !hasLTV && loan.ltvStatus === "NO_PRICE";
  // Tone comes from the backend's own classification against the basket's warning
  // and breach thresholds, not a second formula here — see utils/ltvTone.
  const ltvClass = ltvTone(loan);

  const INFO_TABS = [
    ["כללי",      TAB_GENERAL],
    ["פיננסי",    TAB_FINANCIAL,   canViewFinancial],
    ["בטחונות",   TAB_COLLATERAL,  canViewCollateral],
    ["הערות",     TAB_NOTES,       canViewNotes],
    ["היסטוריה",  TAB_HISTORY,     canViewHistory],
    ["עמלות",     TAB_FEES,        canViewFinancial],
  ].filter(([, , show]) => show !== false);

  const hasFees =
    loan.originationFeePct > 0 ||
    loan.unusedCommitmentFeePct > 0 ||
    loan.prepaymentPenaltyPct > 0 ||
    loan.managementFeePct > 0;

  return (
    <div className="loan-detail-page">
      {/* ── Header ──────────────────────────────────────────── */}
      <div className="loan-detail-header">
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-md)", flex: 1, minWidth: 0 }}>
          <Link to="/" className="btn btn-ghost">← חזרה</Link>
          <div className="loan-detail-title-group">
            <h1 className="loan-detail-title">
              {loan.borrowerCompany || "הלוואה"}
              {loan.lender ? ` ← ${loan.lender}` : ""}
            </h1>
            <div className="loan-detail-meta">
              <span className={`status-badge ${isActive ? "active" : "repaid"}`}>
                <span className="status-dot" />
                {isActive ? "פעיל" : "פורע"}
              </span>
              {loan.classification && (
                <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
                  {CLASSIFICATION_LABELS[loan.classification] || loan.classification}
                </span>
              )}
              {loan.currency && (
                <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
                  · {loan.currency}
                </span>
              )}
            </div>
          </div>
        </div>
        {canEditLoan && <Link to={`/edit/${id}`} className="btn btn-primary">
          עריכת הלוואה
        </Link>}
      </div>

      {/* ── Hero KPI Row ─────────────────────────────────────── */}
      {canViewKpis && <div className="loan-kpi-row">
        <div className="kpi-card accent">
          <div className="kpi-label">קרן הלוואה</div>
          <div className="kpi-value">{formatNumber(loan.principal)}</div>
          <div className="kpi-sub">
            {loan.currency}
            {loan.remainingPrincipal != null &&
              loan.remainingPrincipal !== loan.principal && (
                <> · יתרה: {formatNumber(loan.remainingPrincipal)}</>
              )}
          </div>
        </div>

        <div className="kpi-card kpi-interest">
          <div className="kpi-label">ריבית שנתית</div>
          <div className="kpi-value kpi-value-interest">{formatPct(loan.interestRate)}</div>
          <div className="kpi-sub kpi-interest-breakdown">
            {loan.rateType === "FLOATING" ? (
              <>
                <span className="kpi-rate-base">
                  <Link
                    to={`/asset/base-rate/${loan.baseRate}`}
                    style={{ color: "inherit", textDecoration: "none", borderBottom: "1px dashed currentColor" }}
                  >
                    {loan.baseRateName || "ריבית בסיס"}
                  </Link>
                  {loan.margin != null && loan.margin !== "" && Number(loan.margin) !== 0
                    ? ` ${formatPct(Number(loan.interestRate) - Number(loan.margin))}`
                    : ""}
                </span>
                {loan.margin != null && loan.margin !== "" && Number(loan.margin) !== 0 && (
                  <span className="kpi-rate-spread"> + מרווח {formatPct(loan.margin)}</span>
                )}
              </>
            ) : (
              <span>ריבית קבועה{loan.hasVAT ? " · כולל מע״מ" : ""}</span>
            )}
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-label">תאריך פירעון</div>
          <div className="kpi-value" style={{ fontSize: "1.3rem" }}>
            {formatDate(loan.maturityDate)}
          </div>
          <div className="kpi-sub">
            {maturityDays != null
              ? maturityDays > 0
                ? `${maturityDays.toLocaleString("he-IL")} ימים נותרו`
                : maturityDays === 0
                ? "היום"
                : "תאריך פירעון עבר"
              : "—"}
          </div>
        </div>

        {/* ── LTV KPI ─────────────────────────────── */}
        <div className={`kpi-card kpi-ltv ${
          hasLTV
            ? LTV_TONE_CLASS[ltvClass] || ""
            : hasNoPriceCollateral ? "kpi-ltv-noprice" : ""
        }`}>
          <div className="kpi-label">LTV נוכחי</div>
          {hasLTV ? (
            <>
              <div className="kpi-value">{formatPct(loan.currentLTV)}</div>
              <div className="kpi-sub">
                {ltvClass === "unknown" ? "לא הוגדרו ספי LTV" : ""}
                {ltvClass === "danger" && <span className="kpi-ltv-alert"> ⚠ חריגה</span>}
              </div>
            </>
          ) : hasNoPriceCollateral ? (
            <div className="kpi-value kpi-value-muted">אין מחיר</div>
          ) : (
            <>
              <div className="kpi-value kpi-value-muted">—</div>
              <div className="kpi-sub">אין בטוחה מחושבת</div>
            </>
          )}
        </div>
      </div>}

      {/* ── Info panel (full width) ─────────────────────── */}
      <div className="loan-info-card loan-info-card-full">
        <div className="loan-info-tabs">
          {INFO_TABS.map(([label, tabId]) => (
            <button
              key={tabId}
              className={`loan-info-tab-btn ${infoTab === tabId ? "active" : ""}`}
              onClick={() => setInfoTab(tabId)}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ── Tab: כללי ──────────────────────────────────── */}
        {infoTab === TAB_GENERAL && (
          <div className="info-grid info-grid-wide">
            <div className="info-field">
              <div className="info-label">מלווה</div>
              <div className="info-value strong">{loan.lender || "—"}</div>
            </div>
            <div className="info-field">
              <div className="info-label">חברה לווה</div>
              <div className="info-value strong">{loan.borrowerCompany || "—"}</div>
            </div>
            <div className="info-field">
              <div className="info-label">סיווג</div>
              <div className="info-value">
                <span className="type-badge">
                  {CLASSIFICATION_LABELS[loan.classification] || loan.classification || "—"}
                </span>
                {loan.classification === "OTHER" && loan.classificationOtherReason && (
                  <div className="info-value muted" style={{ marginTop: 4 }}>
                    {loan.classificationOtherReason}
                  </div>
                )}
              </div>
            </div>
            <div className="info-field">
              <div className="info-label">סוג הלוואה</div>
              <div className="info-value">{LOAN_TYPE_LABELS[loan.loanType] || "—"}</div>
            </div>
            <div className="info-field">
              <div className="info-label">תקופת תשלום</div>
              <div className="info-value">{PAYMENT_PERIOD_LABELS[loan.paymentPeriod] || "—"}</div>
            </div>
            <div className="info-field">
              <div className="info-label">פרויקט</div>
              <div className="info-value">{loan.projectName || <span className="muted">—</span>}</div>
            </div>
            {loan.canExtend && (
              <div className="info-field">
                <div className="info-label">אפשרות הארכה</div>
                <div className="info-value">כן</div>
              </div>
            )}
            {loan.hasEarlyRepaymentOption && (
              <div className="info-field">
                <div className="info-label">פירעון מוקדם</div>
                <div className="info-value">
                  כן
                  {loan.earlyRepaymentNoticeDays
                    ? ` · ${loan.earlyRepaymentNoticeDays} ימי הודעה`
                    : ""}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Tab: פיננסי ────────────────────────────────── */}
        {canViewFinancial && infoTab === TAB_FINANCIAL && (
          <div className="info-grid info-grid-wide">
            <div className="info-field">
              <div className="info-label">קרן הלוואה</div>
              <div className="info-value accent">
                {formatNumber(loan.principal)} {loan.currency}
              </div>
            </div>
            <div className="info-field">
              <div className="info-label">יתרת קרן</div>
              <div className="info-value strong">
                {formatNumber(loan.remainingPrincipal ?? loan.principal)} {loan.currency}
              </div>
            </div>
            <div className="info-field">
              <div className="info-label">ריבית כוללת</div>
              <div className="info-value accent">{formatPct(loan.interestRate)}</div>
            </div>
            <div className="info-field">
              <div className="info-label">סוג ריבית</div>
              <div className="info-value">{RATE_TYPE_LABELS[loan.rateType] || "—"}</div>
            </div>
            {loan.baseRateName && (
              <div className="info-field">
                <div className="info-label">ריבית בסיס</div>
                <div className="info-value">
                  <Link to={`/asset/base-rate/${loan.baseRate}`} style={{ color: "var(--accent)", textDecoration: "none" }}>
                    {loan.baseRateName}
                  </Link>
                </div>
              </div>
            )}
            {loan.margin != null && loan.margin !== "" && Number(loan.margin) > 0 && (
              <div className="info-field">
                <div className="info-label">מרווח</div>
                <div className="info-value">{formatPct(loan.margin)}</div>
              </div>
            )}
            <div className="info-field">
              <div className="info-label">תאריך העמדה</div>
              <div className="info-value">{formatDate(loan.originationDate)}</div>
            </div>
            <div className="info-field">
              <div className="info-label">תאריך פירעון</div>
              <div className="info-value strong">{formatDate(loan.maturityDate)}</div>
            </div>
            {loan.hasVAT && (
              <div className="info-field">
                <div className="info-label">מע״מ</div>
                <div className="info-value">כן (18%)</div>
              </div>
            )}
            {loan.dayCountConvention && (
              <div className="info-field">
                <div className="info-label">מוסכמת ימים</div>
                <div className="info-value">{loan.dayCountConvention}</div>
              </div>
            )}
            {hasFees && (
              <div className="fee-chips">
                {loan.originationFeePct > 0 && (
                  <div className="fee-chip">
                    <div className="fee-chip-label">עמלת הקצאה</div>
                    <div className="fee-chip-value">{formatPct(loan.originationFeePct)}</div>
                  </div>
                )}
                {loan.unusedCommitmentFeePct > 0 && (
                  <div className="fee-chip">
                    <div className="fee-chip-label">מסגרת לא משוכה</div>
                    <div className="fee-chip-value">{formatPct(loan.unusedCommitmentFeePct)}</div>
                  </div>
                )}
                {loan.prepaymentPenaltyPct > 0 && (
                  <div className="fee-chip">
                    <div className="fee-chip-label">קנס פירעון מוקדם</div>
                    <div className="fee-chip-value">{formatPct(loan.prepaymentPenaltyPct)}</div>
                  </div>
                )}
                {loan.managementFeePct > 0 && (
                  <div className="fee-chip">
                    <div className="fee-chip-label">דמי ניהול</div>
                    <div className="fee-chip-value">{formatPct(loan.managementFeePct)}</div>
                  </div>
                )}
              </div>
            )}
            {loan.hasRestrictedElement && (
              <>
                <div className="info-field">
                  <div className="info-label">סכום מוגבל</div>
                  <div className="info-value strong">
                    {formatNumber(loan.restrictedElementAmount)} {loan.currency}
                  </div>
                </div>
                <div className="info-field full">
                  <div className="info-label">סיבת ההגבלה</div>
                  <div className="info-value">{loan.restrictedElementReason || "—"}</div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Tab: בטחונות ───────────────────────────────── */}
        {canViewCollateral && infoTab === TAB_COLLATERAL && (
          <div className="info-grid info-grid-wide">
            {/* ── Basket-backed security: singleton edits in place, shared summarizes + links ── */}
            <LoanSecuritySection basket={loan.basket} canEdit={canEditLoan} onSecurityChanged={refetchLoan} />
            {/* Every loan created through POST /loans gets a basket (named or
                singleton), so LoanSecuritySection above covers all security.
                A loan with no basket simply has none recorded. */}
            {!loan.basket && (
              <div className="info-field full">
                <div className="info-value muted">לא הוגדרו בטחונות</div>
              </div>
            )}
          </div>
        )}

        {/* ── Tab: היסטוריה ──────────────────────────────── */}
        {canViewHistory && infoTab === TAB_HISTORY && (
          <div style={{ padding: "var(--space-md) 0" }}>
            {historyLoading ? (
              <div className="info-value muted" style={{ padding: "var(--space-lg)" }}>טוען היסטוריה...</div>
            ) : history.length === 0 ? (
              <div className="info-value muted" style={{ padding: "var(--space-lg)" }}>אין רשומות היסטוריה</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "var(--space-md)" }}>
                {history.map((entry) => {
                  const bg = ACTION_COLORS[entry.action] || "#f9fafb";
                  const changesEntries = entry.changes && typeof entry.changes === "object"
                    ? Object.entries(entry.changes).filter(([k]) => k !== "updated" && k !== "created")
                    : [];
                  return (
                    <div key={entry._id} style={{ display: "flex", gap: 12, padding: "10px 12px", background: "#fafafa", borderRadius: 8, border: "1px solid #f0f0f0" }}>
                      <div style={{ flexShrink: 0, paddingTop: 2 }}>
                        <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600, background: bg }}>
                          {ACTION_LABELS[entry.action] || entry.action}
                        </span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: changesEntries.length ? 6 : 0 }}>
                          <span style={{ fontSize: 13, fontWeight: 600 }}>{entry.userName}</span>
                          <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                            {new Date(entry.createdAt).toLocaleString("he-IL")}
                          </span>
                        </div>
                        {changesEntries.length > 0 && (
                          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                            {changesEntries.map(([field, val]) => {
                              if (val && typeof val === "object" && "old" in val && "new" in val) {
                                return (
                                  <div key={field} style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                                    <span style={{ fontWeight: 500 }}>{field}:</span>{" "}
                                    <span style={{ textDecoration: "line-through", color: "#9ca3af" }}>{String(val.old ?? "—")}</span>
                                    {" → "}
                                    <span style={{ color: "#111" }}>{String(val.new ?? "—")}</span>
                                  </div>
                                );
                              }
                              return (
                                <div key={field} style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                                  <span style={{ fontWeight: 500 }}>{field}:</span> {String(val)}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Tab: עמלות ─────────────────────────────────── */}
        {canViewFinancial && infoTab === TAB_FEES && (
          <LoanFeesTab loanId={id} />
        )}

        {/* ── Tab: הערות ─────────────────────────────────── */}
        {canViewNotes && infoTab === TAB_NOTES && (
          <div className="info-grid info-grid-wide">
            {loan.disclosures && (
              <div className="info-field full">
                <div className="info-label">גילויים</div>
                <div className="info-value" style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                  {loan.disclosures}
                </div>
              </div>
            )}
            {loan.notes && (
              <div className="info-field full">
                <div className="info-label">הערות ספציפיות</div>
                <div className="info-value" style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                  {loan.notes}
                </div>
              </div>
            )}
            {loan.generalNotes && (
              <div className="info-field full">
                <div className="info-label">הערות כלליות</div>
                <div className="info-value" style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                  {loan.generalNotes}
                </div>
              </div>
            )}
            {!loan.disclosures && !loan.notes && !loan.generalNotes && (
              <div className="info-field full">
                <div className="info-value muted">אין הערות להצגה</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Amortization card (full width, split: chart | table) ── */}
      {scheduleLoading ? (
        <div className="amort-card amort-empty-card">
          <div className="amort-empty">טוען לוח סילוקין...</div>
        </div>
      ) : schedule.length > 0 ? (
        <div className="amort-card amort-card-split">
          <div className="amort-split-header">
            <h2 className="amort-title">לוח סילוקין</h2>
            {nextPayment && (
              <div className="amort-next-payment-badge">
                <span className="anp-label">תשלום קרוב</span>
                <span className="anp-value">{formatNumber(nextPayment.payment)} {loan.currency}</span>
                <span className="anp-date">{formatDate(nextPayment.date)}</span>
              </div>
            )}
          </div>
          <div className="amort-split-body">
            {/* Chart pane */}
            {canViewAmortChart && <div className="amort-chart-pane">
              <AmortizationChart
                rows={schedule}
                currency={loan.currency || "ILS"}
              />
            </div>}
            {/* Table pane */}
            {canViewAmortTable && <div className="amort-table-pane">
              <AmortizationTable
                rows={schedule}
                currency={loan.currency || "ILS"}
                noCard
                nextPaymentDate={nextPayment?.date}
              />
            </div>}
          </div>
        </div>
      ) : (
        <div className="amort-card amort-empty-card">
          <div className="amort-empty">
            אין נתוני סילוקין. וודא תאריך התחלה, תאריך פירעון, סוג הלוואה ותקופת תשלום.
          </div>
        </div>
      )}
    </div>
  );
}
